import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {StyledBudgetsRequestPageContainer, StyledBudgetsRequestPageContent} from './StyledBudgetRequestDetailsPage';
import NotFoundPage from '../NotFoundPage';
import PageContainer from '../../components/PageContainer';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import SpinSmall from '../../components/SpinSmall';
import WebSocket from '../../components/WebSocket';
import DashboardPageTransition from '../../components/animations/DashboardPageTransition';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader';
import BudgetRequestTabs from '../../components/pages/BudgetRequestDetailsPage/BudgetRequestTabs';
import BudgetRequestInfo from '../../components/pages/BudgetRequestDetailsPage/BudgetRequestInfo';
import routes from '../../routes/routes.json';
import {budgetActions} from '../../state/actions/budget.actions';
import {budgetHelpers} from '../../utils/helpers';
import {backendWebsocketActionsConstants} from '../../constants';

const getBudgetName = budgetHelpers.getBudgetName;

const BudgetRequestDetailsPage = ({
  ...props
}) => {
  const uriParams = useParams();
  const [t] = useTranslation('main');
  const [pageLoading, setPageLoading] = useState({loading: true, exists: true});
  const dispatch = useDispatch();
  const budget = useSelector(state => state?.budget?.budgetRequest);

  const navigate = useNavigate();

  const title = `${t('pageTitles.budgetsRequests')} | ${getBudgetName(budget)}`;

  const breadcrumbs = [{
    title: t('pageTitles.budgetsRequests'), href: routes.budgetsRequests }, { title: getBudgetName(budget) }
  ];

  const budgetId = useMemo(() => uriParams?.budgetId, [uriParams]);

  const enabledWebSocketConnection = useMemo(() => {
    const isDraft = budgetHelpers.isDraftBudget(budget);
    const isPurchaseOrder = budgetHelpers.isPurchaseOrderBudget(budget);
    return isDraft && isPurchaseOrder;
  }, [budget]);

  useEffect(() => {
    return () => dispatch(budgetActions.clearBudgetRequest());
  }, [dispatch]);

  // if budget status is active redirect to active budget page
  useEffect(() => {
    if (!budget) return;
    if (budgetHelpers.getActiveBudgetStatuses().includes(budget?.status)) {
      navigate(`${routes.budgetsActive}/${budget.id}`);
    }
  }, [budget?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadBudgetRequest(budgetId);
  }, [budgetId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBudgetRequest = () => {
    if (!budgetId) return;

    dispatch(
      budgetActions.getBudgetRequest({
        id: budgetId,
        successCallback: () => setPageLoading({loading: false, exists: true}),
        errorCallback: () => setPageLoading({loading: false, exists: false})
      })
    );
  }

  const handleWebsocketMessage = (data) => {
    const action = data?.action;
    if (!action) return;

    const id = data?.budget_request;
    if (id !== budgetId) return;

    ({
      [backendWebsocketActionsConstants.PO_APPROVERS_ASSIGNED]: () => {
        setPageLoading({loading: true, exists: true});
        loadBudgetRequest(budgetId);
      },
    })[action]?.();
  };

  const {exists, loading} = pageLoading;

  return (
    <PageContainer {...props}>
      <PageDocumentDetails title={title} />
      <BalancePageHeader breadcrumbs={breadcrumbs} />
      <DashboardPageTransition>
        <SpinSmall spinning={loading}>

          {exists ? (
            <StyledBudgetsRequestPageContainer>
              <StyledBudgetsRequestPageContent size='large' direction='vertical'>
                <BudgetRequestInfo />
              </StyledBudgetsRequestPageContent>
              <BudgetRequestTabs />
            </StyledBudgetsRequestPageContainer>
          ) : !loading && <NotFoundPage />}

        </SpinSmall>
      </DashboardPageTransition>

      {enabledWebSocketConnection && <WebSocket onMessage={handleWebsocketMessage} />}

    </PageContainer>
  );
}

export default BudgetRequestDetailsPage;
